/**
 * Phase 6 — Members tab. Lists portalInvitations + lets the owner invite
 * an arbitrary email via Graph `POST /invitations` (Convex action
 * `portalInvitations:inviteByEmail`). For non-Microsoft email recipients
 * Entra automatically falls back to one-time-passcode auth on first open.
 *
 * Phase 7 — adds a public-sharing two-mode toggle (public / disabled) that
 * calls `sharePointSharing:setPortalSharingMode`.
 */

import { useState, useMemo } from 'react';
import { useQuery, useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import { useResolvedPortalSlug } from '../../lib/tenant/portalSlugFromUrl';

const FONT = 'var(--xp-font-body)';

const panelStyle: React.CSSProperties = {
  fontFamily: FONT,
  fontSize: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: 12,
};

const sectionStyle: React.CSSProperties = {
  border: '1px solid #ccc',
  padding: 10,
  background: '#fafafa',
};

const inputStyle: React.CSSProperties = {
  fontFamily: FONT,
  fontSize: 12,
  padding: '3px 6px',
  border: '1px solid #999',
  borderRadius: 0,
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 6,
  marginBottom: 2,
  fontSize: 11,
  color: '#333',
};

const btnPrimary: React.CSSProperties = {
  fontFamily: FONT,
  fontSize: 12,
  padding: '4px 12px',
  marginTop: 8,
  cursor: 'pointer',
};

const statusOk: React.CSSProperties = {
  color: '#0a6f0a',
  marginTop: 6,
  fontSize: 11,
};
const statusErr: React.CSSProperties = {
  color: '#a00',
  marginTop: 6,
  fontSize: 11,
};

type InviteRole = 'owner' | 'member' | 'viewer';

interface InvitationRow {
  _id: string;
  invitedEmail: string;
  displayName?: string;
  role: InviteRole;
  status: 'pending' | 'accepted' | 'failed';
  recipientFlavor?: 'msa' | 'otp' | 'unknown';
  inviteRedeemUrl?: string;
  failureReason?: string;
  createdAt: string;
  sendInvitationMessage: boolean;
}

export function MembersTab(): React.JSX.Element {
  const clientSlug = useResolvedPortalSlug();

  if (clientSlug === null || clientSlug === '') {
    return (
      <div style={panelStyle} data-testid={testIds.settings.membersPanel}>
        <p>
          Members are only available inside a portal. Open a portal first.
        </p>
      </div>
    );
  }

  return <MembersTabInner clientSlug={clientSlug} />;
}

function MembersTabInner({
  clientSlug,
}: {
  clientSlug: string;
}): React.JSX.Element {
  const invitations = useQuery(api.portalInvitations.listForPortal, {
    clientSlug,
  });
  const inviteAction = useAction(api.portalInvitations.inviteByEmail);
  const sharingMode = useQuery(api.sharePointSharing.getPortalSharingMode, {
    clientSlug,
  });
  const setSharingMode = useAction(
    api.sharePointSharing.setPortalSharingMode,
  );

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<InviteRole>('member');
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<
    | { ok: true; flavor: string; redeemUrl?: string }
    | { ok: false; reason: string }
    | null
  >(null);

  const [sharingBusy, setSharingBusy] = useState(false);
  const [sharingStatus, setSharingStatus] = useState<string | null>(null);

  const sortedInvitations = useMemo<InvitationRow[]>(() => {
    if (invitations === undefined || invitations === null) return [];
    return [...invitations] as InvitationRow[];
  }, [invitations]);

  const emailLooksValid = useMemo(() => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }, [email]);

  async function handleInvite(): Promise<void> {
    if (!emailLooksValid) {
      setStatus({ ok: false, reason: 'Enter a valid email address.' });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const result = await inviteAction({
        clientSlug,
        email: email.trim(),
        displayName: displayName.trim() || undefined,
        role,
        sendInvitationMessage: sendEmail,
      });
      if (result.ok) {
        setStatus({
          ok: true,
          flavor: result.recipientFlavor,
          redeemUrl: result.inviteRedeemUrl,
        });
        setEmail('');
        setDisplayName('');
      } else {
        setStatus({ ok: false, reason: result.reason });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus({ ok: false, reason: msg });
    } finally {
      setBusy(false);
    }
  }

  async function handleSetSharing(mode: 'public' | 'disabled'): Promise<void> {
    setSharingBusy(true);
    setSharingStatus(null);
    try {
      const result = await setSharingMode({ clientSlug, mode });
      if (result.ok) {
        setSharingStatus(
          `Sharing set to ${mode}. ${
            result.revokedCount === undefined
              ? ''
              : `${result.revokedCount} link(s) revoked.`
          }`,
        );
      } else {
        setSharingStatus(`Failed: ${result.reason}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSharingStatus(`Error: ${msg}`);
    } finally {
      setSharingBusy(false);
    }
  }

  const currentMode = sharingMode?.mode ?? 'public';

  return (
    <div style={panelStyle} data-testid={testIds.settings.membersPanel}>
      {/* Public sharing two-mode toggle (Phase 7) */}
      <div style={sectionStyle}>
        <strong>Public sharing for this portal</strong>
        <p style={{ marginTop: 6, marginBottom: 8, color: '#444' }}>
          When public, anyone with a sample-doc link can open it without
          signing in. When disabled, every share link is revoked and the
          “Share” buttons hide.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            style={{
              ...btnPrimary,
              marginTop: 0,
              fontWeight: currentMode === 'public' ? 'bold' : 'normal',
            }}
            onClick={() => void handleSetSharing('public')}
            disabled={sharingBusy || currentMode === 'public'}
            data-testid={testIds.settings.membersSharingTogglePublic}
          >
            Public {currentMode === 'public' ? '✓' : ''}
          </button>
          <button
            type="button"
            style={{
              ...btnPrimary,
              marginTop: 0,
              fontWeight: currentMode === 'disabled' ? 'bold' : 'normal',
            }}
            onClick={() => void handleSetSharing('disabled')}
            disabled={sharingBusy || currentMode === 'disabled'}
            data-testid={testIds.settings.membersSharingToggleDisabled}
          >
            Disabled {currentMode === 'disabled' ? '✓' : ''}
          </button>
          {sharingBusy && <span style={{ fontSize: 11 }}>Working…</span>}
        </div>
        {sharingStatus !== null && (
          <div
            style={statusOk}
            data-testid={testIds.settings.membersSharingStatus}
          >
            {sharingStatus}
          </div>
        )}
      </div>

      {/* Invite new member */}
      <div style={sectionStyle}>
        <strong>Invite someone by email</strong>
        <p style={{ marginTop: 6, marginBottom: 4, color: '#444' }}>
          Microsoft sends an invitation to the recipient. People without a
          Microsoft account get a one-time code emailed by Microsoft on
          first open.
        </p>

        <label style={labelStyle} htmlFor="members-email">
          Email
        </label>
        <input
          id="members-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
          placeholder="name@example.com"
          data-testid={testIds.settings.membersEmailInput}
        />

        <label style={labelStyle} htmlFor="members-displayname">
          Display name (optional)
        </label>
        <input
          id="members-displayname"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          style={inputStyle}
          placeholder="Alice Lee"
          data-testid={testIds.settings.membersDisplayNameInput}
        />

        <label style={labelStyle} htmlFor="members-role">
          Role
        </label>
        <select
          id="members-role"
          value={role}
          onChange={(e) => setRole(e.target.value as InviteRole)}
          style={inputStyle}
          data-testid={testIds.settings.membersRoleSelect}
        >
          <option value="viewer">Viewer (read-only)</option>
          <option value="member">Member (read + write)</option>
          <option value="owner">Owner (full control)</option>
        </select>

        <label style={{ ...labelStyle, marginTop: 8 }}>
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            data-testid={testIds.settings.membersSendEmailCheckbox}
          />
          {' '}Send Microsoft invitation email
        </label>

        <button
          type="button"
          style={btnPrimary}
          onClick={() => void handleInvite()}
          disabled={busy || !emailLooksValid}
          data-testid={testIds.settings.membersSubmitButton}
        >
          {busy ? 'Inviting…' : 'Send invitation'}
        </button>

        {status !== null && status.ok && (
          <div
            style={statusOk}
            data-testid={testIds.settings.membersInviteStatus}
          >
            Invitation sent. Recipient flavor: {status.flavor}.{' '}
            {status.redeemUrl !== undefined && (
              <>
                {' '}
                <a
                  href={status.redeemUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Redeem URL
                </a>
              </>
            )}
          </div>
        )}
        {status !== null && !status.ok && (
          <div
            style={statusErr}
            data-testid={testIds.settings.membersInviteStatus}
          >
            Failed: {status.reason}
          </div>
        )}
      </div>

      {/* Invitation history */}
      <div style={sectionStyle}>
        <strong>Invitations</strong>
        {invitations === undefined && (
          <p style={{ marginTop: 6 }}>Loading…</p>
        )}
        {invitations !== undefined && sortedInvitations.length === 0 && (
          <p style={{ marginTop: 6, color: '#666' }}>
            No invitations yet. Send one above.
          </p>
        )}
        {sortedInvitations.length > 0 && (
          <table
            style={{
              marginTop: 6,
              width: '100%',
              fontSize: 11,
              borderCollapse: 'collapse',
            }}
          >
            <thead>
              <tr style={{ background: '#eee' }}>
                <th style={cellStyle}>Email</th>
                <th style={cellStyle}>Role</th>
                <th style={cellStyle}>Flavor</th>
                <th style={cellStyle}>Status</th>
                <th style={cellStyle}>When</th>
              </tr>
            </thead>
            <tbody>
              {sortedInvitations.map((row) => (
                <tr
                  key={row._id}
                  data-testid={testIds.settings.membersRow(row._id)}
                >
                  <td style={cellStyle}>{row.invitedEmail}</td>
                  <td style={cellStyle}>{row.role}</td>
                  <td style={cellStyle}>{row.recipientFlavor ?? '—'}</td>
                  <td style={cellStyle}>{row.status}</td>
                  <td style={cellStyle}>
                    {new Date(row.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const cellStyle: React.CSSProperties = {
  padding: '3px 6px',
  borderBottom: '1px solid #ddd',
  textAlign: 'left',
  verticalAlign: 'top',
};
